// MeetForm.js - Updated to support program groups
import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import styles from './MeetForm.module.css';

import { statusOptions } from 'config/admin/meet';
import {
  clubFeaturesOf,
  clubForId,
  meetTypeOptionsFor,
  organisationTitleOf,
  organisationWordOf,
} from 'utils/clubFeatures';
import { GROUPING_BASIS, groupingBasisOf } from 'utils/groupLabel';
import { copyPointsRules, pointsRulesShortLine } from 'utils/pointsRules';
import { teamWordsOf } from 'utils/teamLabel';

import { getVolunteerRoles } from 'api/volunteerApi';
import { getClubEventTypes } from 'api/club/eventTypeApi';

import CustomInput from 'components/common/CustomInput/CustomInput';
import CustomDatePicker from 'components/common/CustomDatePicker';
import CustomTimePicker from 'components/common/CustomTimePicker';
import CustomSelect from 'components/common/CustomSelect';
import CustomButton, {
  defaultButtonStyle,
} from 'components/common/CustomButton';
import VolunteerRequirements from 'components/admin/volunteer/VolunteerRequirements';
import ProgramDesigner from 'components/admin/ProgramDesigner/ProgramDesigner';
import HighJumpSettings from 'components/admin/HighJumpSettings';
import MaxEventsPerGroup from 'components/admin/MaxEventsPerGroup';
import MeetSignupSettings from 'components/admin/MeetSignupSettings';
import MeetSignupStatusBadge from 'components/admin/MeetSignupStatusBadge';
import ReplayDelayNote from 'components/admin/ReplayDelayNote';
import PointsRulesEditor from 'components/admin/PointsRulesEditor';
import { getClubProgramGroups } from 'api/clubApi';
import { getTimezones } from 'api/generalApi';
import { requirementResolvesToProgram } from 'utils/volunteerProgramUtils';

const MeetForm = ({
  clubId,
  mode,
  meetId,
  formValues,
  loading = false,
  errors,
  handleInputChange,
  handleTimeChange,
  handleButton,
  sourceMeet = null,
}) => {
  // The club this meet belongs to, from the club list already in memory. It decides the meet types offered
  // (a school has Competition and Training Session, and no multi-club meet) and whether high jump minimum
  // starting heights are shown (they belong to an age, so not for a club that groups by year level).
  const club = useSelector((state) => clubForId(state.user, clubId));
  const meetTypeOptions = meetTypeOptionsFor(club, 'form', formValues.meetType);
  const groupsByYearLevel = groupingBasisOf(club) === GROUPING_BASIS.YEAR_LEVEL;
  // The meet's own points: a school is not offered season-best points, and reads "the school's points".
  const { seasonBestPoints, teams: hasHouses } = clubFeaturesOf(club);
  const organisationWord = organisationWordOf(club);
  // The page the club's points are set on: Club Settings, or School Settings for a school.
  const settingsTitle = `${organisationTitleOf(club)} Settings`;

  const hasUserModifiedRequirements = React.useRef(false);
  const ingestedRequirementsRef = React.useRef(null);
  const [volunteerRoles, setVolunteerRoles] = useState([]);
  const [requirements, setRequirements] = useState({});
  const [volunteerLoading, setVolunteerLoading] = useState(false);
  const [clubProgramGroups, setClubProgramGroups] = useState({
    ageGroups: [],
    athleteGroups: [],
  });
  const [clubEventTypes, setClubEventTypes] = useState([]);
  const [timezoneOptions, setTimezoneOptions] = useState([]);

  // IANA timezone list for the per-meet timezone picker (seeded from the club default — §6.8 / D11).
  useEffect(() => {
    const loadTimezones = async () => {
      try {
        const res = await getTimezones();
        setTimezoneOptions(
          (res.data || []).map((tz) => ({ value: tz.id, label: tz.displayName }))
        );
      } catch (err) {
        console.log('[MeetForm] Fetch timezones error: ', err);
      }
    };
    loadTimezones();
  }, []);

  const [meetProgram, setMeetProgram] = useState(
    () => formValues.programData || { rotationCount: 4, programGroups: [] }
  );

  useEffect(() => {
    // Only update if actually different to prevent loops
    if (
      formValues.programData &&
      JSON.stringify(formValues.programData) !== JSON.stringify(meetProgram)
    ) {
      setMeetProgram(formValues.programData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues.programData]); // Don't include meetProgram in dependencies

  const programGroups = useMemo(() => {
    //console.log('MeetForm programGroups memo input:', meetProgram);
    if (!meetProgram.programGroups || meetProgram.programGroups.length === 0) {
      return [];
    }
    const result = meetProgram.programGroups
      .filter((group) => group.groupSource && group.displayName)
      .map((group) => ({
        displayName: group.displayName || group.groupName,
        groupSource: group.groupSource,
        sourceGroupID: group.sourceGroupID,
        ageRange: group.ageRange,
        gender: group.gender,
        displayOrder: group.displayOrder,
        memberCount: group.athletes ? group.athletes.length : null,
        events: (group.events || []).map((event) => ({
          ...event,
          eventType:
            event.eventType === ''
              ? null // Change empty string to null
              : typeof event.eventType === 'string'
                ? parseInt(event.eventType, 10)
                : event.eventType,
        })),
      }));
    //console.log('MeetForm programGroups memo output:', result);
    return result;
  }, [meetProgram]);

  const isTrainingSession = parseInt(formValues.meetType) === 3;

  // When switching to Training Session, uncheck season points and season bests
  useEffect(() => {
    if (isTrainingSession && formValues.countsForSeasonPoints) {
      handleInputChange({
        target: { name: 'countsForSeasonPoints', value: false }
      });
    }
    if (isTrainingSession && formValues.countsForSeasonBests) {
      handleInputChange({
        target: { name: 'countsForSeasonBests', value: false }
      });
    }
  }, [isTrainingSession]); // eslint-disable-line react-hooks/exhaustive-deps

  // Check if High Jump (event type 61) is in the program
  const hasHighJump = useMemo(() => {
    if (!programGroups || programGroups.length === 0) return false;
    
    return programGroups.some(group => 
      group.events && group.events.some(event => 
        event.eventType === 61 || event.eventType === '61'
      )
    );
  }, [programGroups]);

  const fetchClubProgramGroups = useCallback(async () => {
    if (!clubId) return null;
    try {
      const response = await getClubProgramGroups(clubId);
      return response.data;
    } catch (err) {
      console.log('[MeetForm] Fetch club age groups error: ', err);
      return null;
    }
  }, [clubId]);

  const fetchClubEventTypes = useCallback(async () => {
    if (!clubId) return null;
    try {
      const response = await getClubEventTypes(clubId, false);
      return response.data;
    } catch (err) {
      console.log('[MeetForm] Fetch club event types error: ', err);
      return null;
    }
  }, [clubId]);

  useEffect(() => {
    fetchClubProgramGroups().then((programGroups) => {
      if (programGroups) {
        setClubProgramGroups(programGroups);
      }
    });
    fetchClubEventTypes().then((eventTypes) => {
      if (eventTypes) {
        setClubEventTypes(eventTypes);
      }
    });
  }, [fetchClubProgramGroups, fetchClubEventTypes]);

  const fetchVolunteerRoles = useCallback(async () => {
    if (!clubId) return;

    setVolunteerLoading(true);
    try {
      const response = await getVolunteerRoles(clubId, false);
      setVolunteerRoles(response.data);
    } catch (err) {
      console.log('[MeetForm] Fetch volunteer roles error: ', err);
      toast.error('Failed to load volunteer roles');
    } finally {
      setVolunteerLoading(false);
    }
  }, [clubId]);

  // Initialize requirements from consolidated API data (for editing existing
  // meet or copying from meet). Each volunteerRequirements payload is ingested
  // exactly once (tracked by reference): this effect also fires when the
  // program is edited (programGroups feeds the ageGroupCode lookup), and
  // rebuilding the map from the original payload then would silently revert
  // every requirement edit made since load/copy.
  useEffect(() => {
    if (
      !formValues.volunteerRequirements ||
      formValues.volunteerRequirements.length === 0 ||
      volunteerRoles.length === 0
    )
      return;
    if (ingestedRequirementsRef.current === formValues.volunteerRequirements)
      return;
    ingestedRequirementsRef.current = formValues.volunteerRequirements;

    // Transform saved requirements into component format
    const transformedRequirements = formValues.volunteerRequirements.reduce(
      (acc, req) => {
        let requirementKey;

        if (req.programGroupName && req.groupType) {
          requirementKey = `${req.volunteerRoleID}_${req.programGroupName}_${req.groupType}`;
        } else if (req.ageGroupCode) {
          requirementKey = `${req.volunteerRoleID}_${req.ageGroupCode}_AgeGroup`;
        } else {
          requirementKey = req.volunteerRoleID;
        }

        // Always get ageGroupCode from the matching program group's ageRange
        let ageGroupCode = req.ageGroupCode || null;
        if (req.programGroupName && req.groupType) {
          const matchingGroup = programGroups.find(
            (group) =>
              group.displayName === req.programGroupName &&
              group.groupSource === req.groupType
          );
          if (matchingGroup && matchingGroup.ageRange) {
            ageGroupCode = matchingGroup.ageRange; // Always use ageRange from program group
          }
        }

        acc[requirementKey] = {
          meetVolunteerRequirementID: req.meetVolunteerRequirementID,
          volunteerRoleID: req.volunteerRoleID,
          roleName: req.roleName || null,
          include: req.numberRequired > 0,
          numberRequired: req.numberRequired,
          numberCommitted: req.numberCommitted || 0,
          programGroupName: req.programGroupName || null,
          groupType: req.groupType || null,
          ageGroupCode: ageGroupCode, // This will now always be the ageRange
          manuallySet: true,
        };
        return acc;
      },
      {}
    );

    setRequirements(transformedRequirements);
    setVolunteerLoading(false);
  }, [formValues.volunteerRequirements, volunteerRoles, programGroups]);

  // Keep each group-linked row's legacy ageGroupCode aligned with its program
  // group's current ageRange (age-group-lifecycle re-save adoption) without
  // rebuilding the map, so requirement edits survive program changes. Rows
  // whose group no longer exists are left untouched here: ID-carrying rows
  // are legacy-tolerated by the server, and ID-less rows are dropped by the
  // save-time program filter in handleSaveClick. IDENTITY-STABLE: returns the
  // previous map when nothing changed.
  useEffect(() => {
    if (programGroups.length === 0) return;
    setRequirements((prev) => {
      let changed = false;
      const next = {};
      for (const [key, req] of Object.entries(prev)) {
        const group =
          req?.programGroupName && req?.groupType
            ? programGroups.find(
                (g) =>
                  g.displayName === req.programGroupName &&
                  g.groupSource === req.groupType
              )
            : null;
        if (group?.ageRange && group.ageRange !== req.ageGroupCode) {
          next[key] = { ...req, ageGroupCode: group.ageRange };
          changed = true;
        } else {
          next[key] = req;
        }
      }
      return changed ? next : prev;
    });
  }, [programGroups]);

  useEffect(() => {
    fetchVolunteerRoles();
  }, [fetchVolunteerRoles]);

  // §6.8 — when self-service signup is enabled and no window is set yet, seed sensible defaults from the meet date:
  // open 17:00 thirty days before, close 17:00 the day before. Only fills when BOTH are empty, so it never
  // overwrites an admin's edits (and the meet-date-change notice below nudges them to review if the date moves).
  useEffect(() => {
    if (
      parseInt(formValues.meetType, 10) === 2 &&
      formValues.selfServiceRegistrationEnabled &&
      formValues.date &&
      !formValues.signupOpenDate &&
      !formValues.signupCloseDate
    ) {
      const meetDay = new Date(`${formValues.date}T00:00:00`);
      const open = new Date(meetDay);
      open.setDate(open.getDate() - 30);
      const close = new Date(meetDay);
      close.setDate(close.getDate() - 1);
      const fmt = (d) =>
        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      handleInputChange({ target: { name: 'signupOpenDate', value: fmt(open) } });
      handleInputChange({ target: { name: 'signupOpenTime', value: '17:00' } });
      handleInputChange({ target: { name: 'signupCloseDate', value: fmt(close) } });
      handleInputChange({ target: { name: 'signupCloseTime', value: '17:00' } });
    }
  }, [
    formValues.meetType,
    formValues.selfServiceRegistrationEnabled,
    formValues.date,
    formValues.signupOpenDate,
    formValues.signupCloseDate,
    handleInputChange,
  ]);

  // §6.8 — if the meet date changes after a signup window is set, that window may now be wrong; nudge the admin.
  const prevMeetDateRef = React.useRef(null);
  useEffect(() => {
    const prev = prevMeetDateRef.current;
    if (
      prev &&
      prev !== formValues.date &&
      parseInt(formValues.meetType, 10) === 2 &&
      formValues.selfServiceRegistrationEnabled &&
      (formValues.signupOpenDate || formValues.signupCloseDate)
    ) {
      toast.info('Meet date changed — check the registration open/close dates in Meet Signup.');
    }
    prevMeetDateRef.current = formValues.date;
  }, [
    formValues.date,
    formValues.meetType,
    formValues.selfServiceRegistrationEnabled,
    formValues.signupOpenDate,
    formValues.signupCloseDate,
  ]);

  // Age-group program rows adopt the club's CURRENT age range whenever the
  // program is edited, so a re-save picks up range changes made in club
  // settings (age-group-lifecycle-design.md §4 Tier 2 / §7.4) — guarded
  // server-side by the program-edit impact review. Names are deliberately NOT
  // adopted here: the designer already displays the live name via identity
  // binding, and pushing a renamed group name through the program save would
  // race the server's requirement rename cascade against the requirements PUT
  // that follows it in the same save. Rows whose source no longer matches a
  // live option (deleted group, gender-split change) are left exactly as
  // loaded — the snapshot persists unchanged.
  // IDENTITY-STABLE: returns the exact same array when nothing adopts. The
  // designer↔form sync loop terminates by reference equality (the designer's
  // setProgramGroups bails out when handed the same array back), and the
  // volunteer-requirements init effect keys on the programGroups memo — a
  // gratuitous new array here re-fires both, resetting in-session requirement
  // edits and flashing the page on a ~400ms cycle.
  const adoptLiveAgeRanges = useCallback(
    (groups) => {
      if (!groups || groups.length === 0) {
        return groups;
      }
      let changed = false;
      const adopted = groups.map((group) => {
        if (group.groupSource !== 'AgeGroup' || !group.sourceGroupID) {
          return group;
        }
        const live = (clubProgramGroups?.ageGroups || []).find(
          (opt) =>
            opt.sourceGroupID === group.sourceGroupID &&
            (opt.gender || 'Mixed') === (group.gender || 'Mixed')
        );
        if (!live || !live.ageRange || live.ageRange === group.ageRange) {
          return group;
        }
        changed = true;
        return { ...group, ageRange: live.ageRange };
      });
      return changed ? adopted : groups;
    },
    [clubProgramGroups]
  );

  const handleProgramDataChange = useCallback(
    (data) => {
      const adoptedGroups = adoptLiveAgeRanges(data?.programGroups);
      const adopted =
        adoptedGroups === data?.programGroups
          ? data
          : { ...data, programGroups: adoptedGroups };
      setMeetProgram(adopted);

      // Update formValues.programData for change detection
      handleInputChange({
        target: {
          name: 'programData',
          value: adopted,
        },
      });
    },
    [handleInputChange, adoptLiveAgeRanges]
  );

  // §6.4 — per-group max-events edits from the MaxEventsPerGroup card. Writes back through the same program-data
  // handler the designer uses, so both edit one shared program state.
  const handleMaxEventsForGroupChange = useCallback(
    (updatedGroups) => {
      handleProgramDataChange(
        { ...meetProgram, programGroups: updatedGroups },
        ''
      );
    },
    [meetProgram, handleProgramDataChange]
  );

  // Handle requirement changes with new structure
  const handleRequirementChange = useCallback(
    (roleId, newRequirement, requirementKey) => {
      if (!hasUserModifiedRequirements.current) {
        hasUserModifiedRequirements.current = true;

        handleInputChange({
          target: {
            name: 'requirementsModified',
            value: true,
          },
        });
      }

      setRequirements((prev) => {
        const existingReq = prev[requirementKey] || {};
        const updated = {
          ...prev,
          [requirementKey]: {
            ...newRequirement,
            volunteerRoleID: roleId,
            manuallySet: true,
            // Preserve ageGroupCode if it exists in either place
            ageGroupCode:
              newRequirement.ageGroupCode || existingReq.ageGroupCode || null,
          },
        };
        return updated;
      });
    },
    [handleInputChange]
  );

  // Handle high jump settings changes
  const handleHighJumpSettingsChange = useCallback(
    (newSettings) => {
      handleInputChange({
        target: {
          name: 'highJumpSettings',
          value: newSettings,
        },
      });
    },
    [handleInputChange]
  );

  // Save-time mirror of the server's RequirementNotInProgram guard
  // (program-edit-impact-design.html §8.1). NEW rows (no
  // MeetVolunteerRequirementID — e.g. copied from a source meet) are dropped
  // when they no longer resolve to the program as it stands at save time,
  // instead of 400-failing the whole save. The drop is always silent: the
  // requirements UI already hides every such row (group sections and Event
  // Officials filter by group presence, age overlap, and event match), so
  // this only stops hidden state being sent — the saved meet matches the
  // screen. Rows carrying an ID always stay in the request — omitting them
  // would delete them server-side.
  const handleSaveClick = () => {
    const filtered = {};
    Object.entries(requirements).forEach(([key, req]) => {
      if (!req?.include || req.meetVolunteerRequirementID) {
        filtered[key] = req;
        return;
      }
      const role = volunteerRoles.find(
        (r) => r.volunteerRoleID === req.volunteerRoleID
      );
      if (
        role &&
        requirementResolvesToProgram(req, role, programGroups, clubEventTypes)
      ) {
        filtered[key] = req;
      }
    });
    if (Object.keys(filtered).length !== Object.keys(requirements).length) {
      setRequirements(filtered);
    }
    handleButton(filtered);
  };

  return (
    <div className={styles.formContainer}>
      {/* Basic Information Card */}
      <div className={styles.formCard}>
        <div className={styles.cardHeader}>
          <h3 className={styles.cardTitle}>Basic Information</h3>
          {parseInt(formValues.meetType) === 2 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <MeetSignupStatusBadge
                selfServiceEnabled={formValues.selfServiceRegistrationEnabled}
                publicEnabled={formValues.publicSignupEnabled}
                signupCode={formValues.signupCode}
              />
            </div>
          )}
        </div>
        {sourceMeet && (
          <div className={styles.sourceMeetBanner}>
            <div className={styles.sourceMeetIcon}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 18.3333C14.6024 18.3333 18.3334 14.6024 18.3334 10C18.3334 5.39763 14.6024 1.66667 10 1.66667C5.39765 1.66667 1.66669 5.39763 1.66669 10C1.66669 14.6024 5.39765 18.3333 10 18.3333Z" stroke="#0284c7" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M10 6.66667V10" stroke="#0284c7" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M10 13.3333H10.0083" stroke="#0284c7" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className={styles.sourceMeetText}>
              <span className={styles.sourceMeetLabel}>Settings copied from: </span>
              <span className={styles.sourceMeetName}>{sourceMeet.meetName}</span>
              {sourceMeet.seasonName && (
                <span className={styles.sourceMeetSeason}>
                  {' '}
                  — {sourceMeet.seasonName}
                </span>
              )}
            </div>
          </div>
        )}
        <div className={styles.formGrid}>
          <CustomSelect
            label="Meet Type"
            options={meetTypeOptions}
            value={formValues.meetType}
            onChange={handleInputChange}
            error={errors.meetType}
            name="meetType"
          />
          <CustomInput
            label="Meet Title"
            placeholder="Enter the meet title"
            name="meetName"
            value={formValues.meetName}
            onChange={handleInputChange}
            error={errors.meetName}
          />
          <CustomDatePicker
            label="Meet Date"
            name="date"
            onChange={handleInputChange}
            error={errors.date}
            value={formValues.date}
          />
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '16px', flexWrap: 'wrap' }}>
            <CustomTimePicker
              label="Start Time"
              placeholder="00:00"
              error={errors.time}
              onTimeChange={handleTimeChange}
              value={formValues.time}
              style={{ marginBottom: '20px' }}
            />
            <div style={{ flex: 1, minWidth: '160px' }}>
              <CustomSelect
                label="Timezone"
                options={timezoneOptions}
                value={formValues.timezone}
                onChange={handleInputChange}
                name="timezone"
              />
            </div>
          </div>
        </div>
        <div className={styles.formRow}>
          <CustomInput
            label="Location"
            name="location"
            error={errors.location}
            onChange={handleInputChange}
            value={formValues.location}
          />
        </div>
        <div className={styles.formRow}>
          <CustomInput
            label="Meet Description"
            placeholder="Describe the meet details..."
            name="description"
            value={formValues.description}
            onChange={handleInputChange}
            error={errors.description}
          />
        </div>
      </div>

      {/* Meet Signup Card - multi-club meets only (§6.8). Sits directly below Basic Information. */}
      {parseInt(formValues.meetType) === 2 && (
        <div className={styles.formCard}>
          <MeetSignupSettings
            selfServiceEnabled={formValues.selfServiceRegistrationEnabled}
            publicEnabled={formValues.publicSignupEnabled}
            signupCode={formValues.signupCode}
            openDate={formValues.signupOpenDate}
            openTime={formValues.signupOpenTime}
            closeDate={formValues.signupCloseDate}
            closeTime={formValues.signupCloseTime}
            registrationCap={formValues.registrationCap}
            errors={errors}
            onChange={(name, value) => handleInputChange({ target: { name, value } })}
          />
        </div>
      )}

      {/* Age Group Event Configuration Card */}
      <div className={styles.formCard}>
        <ProgramDesigner
          clubId={clubId}
          programData={meetProgram}
          clubProgramGroups={clubProgramGroups}
          clubEventTypes={clubEventTypes}
          onChange={handleProgramDataChange}
          onImportClick={() => {
            console.log('Import from previous meet - coming soon');
          }}
          onCheckVolunteerImpact={async () => {
            if (mode === 'edit' && meetId) {
              return { hasSignups: false, count: 0 }; // Placeholder
            }
            return { hasSignups: false, count: 0 };
          }}
        />
      </div>

      {/* Max Events Per Group Card - multi-club meets only (§6.4) */}
      {parseInt(formValues.meetType) === 2 && (
        <div className={styles.formCard}>
          <MaxEventsPerGroup
            programGroups={meetProgram?.programGroups || []}
            rotationCount={meetProgram?.rotationCount || 0}
            onChange={handleMaxEventsForGroupChange}
          />
        </div>
      )}

      {/* High Jump Settings Card - Only show if High Jump is in the program and not a training session */}
      {hasHighJump && !isTrainingSession && (
        <div className={styles.formCard}>
          <HighJumpSettings
            highJumpSettings={formValues.highJumpSettings}
            onChange={handleHighJumpSettingsChange}
            showMinimumHeights={!groupsByYearLevel}
          />
        </div>
      )}

      {/* Volunteer Requirements Card with new structure */}
      {!volunteerLoading &&
        volunteerRoles.length > 0 &&
        programGroups.length > 0 && (
          <div className={styles.formCard}>
            <VolunteerRequirements
              volunteerRoles={volunteerRoles}
              programGroups={programGroups}
              clubEventTypes={clubEventTypes}
              requirements={requirements}
              onRequirementChange={handleRequirementChange}
            />
          </div>
        )}

      {/* Season Points Settings Card - Hidden for training sessions. A school reads "school records" and "your
          school's" (Doug, 2026-10-26). */}
      {!isTrainingSession && <div className={styles.formCard}>
        <h3 className={styles.cardTitle}>Points and Season Bests</h3>
        <p className={styles.cardSubtitle}>
          Configure how this meet contributes to season points, season bests and {organisationWord} records. You can exclude this meet from points, from season bests and {organisationWord} records, or from both, and use your {organisationWord}&apos;s default points settings or create custom points for this meet.
        </p>

        {/* Main checkbox - Count for season points. Season bests follow it (they normally match), and
            can then be changed on their own with the second checkbox. */}
        <div className={styles.checkboxRow}>
          <label className={styles.checkboxLabel}>
            <input
              type="checkbox"
              name="countsForSeasonPoints"
              checked={formValues.countsForSeasonPoints ?? true}
              onChange={(e) => {
                handleInputChange({
                  target: { name: 'countsForSeasonPoints', value: e.target.checked }
                });
                handleInputChange({
                  target: { name: 'countsForSeasonBests', value: e.target.checked }
                });
              }}
              className={styles.checkbox}
            />
            <span>Count this meet for season points</span>
          </label>
        </div>

        {/* Info message when not counting for points */}
        {!formValues.countsForSeasonPoints && (
          <div className={styles.infoMessage}>
            <span style={{ fontSize: '14px', color: '#6b7280' }}>
              This meet will not count towards season points. Results will still be tracked and visible.
            </span>
          </div>
        )}

        {/* Second checkbox - Count for season bests */}
        <div className={styles.checkboxRow}>
          <label className={styles.checkboxLabel}>
            <input
              type="checkbox"
              name="countsForSeasonBests"
              checked={formValues.countsForSeasonBests ?? true}
              onChange={(e) => {
                handleInputChange({
                  target: { name: 'countsForSeasonBests', value: e.target.checked }
                });
              }}
              className={styles.checkbox}
            />
            <span>Count this meet for season bests and {organisationWord} records</span>
          </label>
        </div>

        {/* Info message when not counting for season bests. The box also decides club records
            (docs/club-records-redesign.md in the portal repo, section 2), so the message says what
            happens to them before Save (Doug, 2026-10-23: a setting that changes other data says so). */}
        {!(formValues.countsForSeasonBests ?? true) && (
          <div className={styles.infoMessage}>
            <span style={{ fontSize: '14px', color: '#6b7280' }}>
              Results at this meet will not be starred, will not set or beat season bests, and cannot become {organisationWord} records. Any of its results under Pending Records are taken off within an hour, and the next best result from a meet that counts takes its place. Approved {organisationWord} records are not changed. Results still count for all-time personal bests.
            </span>
          </div>
        )}

        {/* The meet's points: the club's, or its own in the same settings as Club Settings
            (docs/school-carnivals-points-slice-2.md in the portal repo). Only shown when the meet
            counts for season points. */}
        {(formValues.countsForSeasonPoints ?? true) && (
        <div className={styles.radioGroup}>
          <div className={styles.radioOption}>
            <input
              type="radio"
              id="useDefaultPoints"
              name="pointsType"
              value="default"
              checked={!formValues.useCustomPoints}
              onChange={() => handleInputChange({
                target: { name: 'useCustomPoints', value: false }
              })}
            />
            <label htmlFor="useDefaultPoints">
              Use the {organisationWord}&apos;s default points
              <span className={styles.radioHelp}>
                {pointsRulesShortLine(formValues.defaultPointsRules, settingsTitle)}
              </span>
            </label>
          </div>
          <div className={styles.radioOption}>
            <input
              type="radio"
              id="useCustomPoints"
              name="pointsType"
              value="custom"
              checked={formValues.useCustomPoints}
              onChange={() => {
                // The first time, start from the club's points. A meet's own points already
                // chosen are kept, so switching back and forth loses nothing.
                if (!formValues.customPointsRules && formValues.defaultPointsRules) {
                  handleInputChange({
                    target: {
                      name: 'customPointsRules',
                      value: copyPointsRules(formValues.defaultPointsRules)
                    }
                  });
                }
                handleInputChange({
                  target: { name: 'useCustomPoints', value: true }
                });
              }}
            />
            <label htmlFor="useCustomPoints">
              Use different points for this meet
              <span className={styles.radioHelp}>
                Opens the same settings for this meet only. The settings in {settingsTitle} are not changed.
              </span>
            </label>
          </div>
        </div>
        )}

        {/* The meet's own points - shown when it uses them and counts for season points */}
        {(formValues.countsForSeasonPoints ?? true) && formValues.useCustomPoints && (
          <div className={styles.pointsSettingsSection}>
            <PointsRulesEditor
              rules={formValues.customPointsRules}
              onChange={(customPointsRules) => handleInputChange({
                target: { name: 'customPointsRules', value: customPointsRules }
              })}
              seasonBestPoints={seasonBestPoints}
              hasHouses={hasHouses}
              teamWords={teamWordsOf(club)}
              idPrefix="meet-points"
            />
          </div>
        )}

        <ReplayDelayNote organisationWord={organisationWord} />
      </div>}

      {/* Status Card */}
      <div className={styles.formCard}>
        <h3 className={styles.cardTitle}>Status</h3>
        <div className={styles.formRow}>
          <CustomSelect
            label="Meet Status"
            options={statusOptions}
            value={formValues.meetStatus}
            onChange={handleInputChange}
            error={errors.meetStatus}
            name="meetStatus"
          />
        </div>
      </div>

      {/* Actions */}
      <div className={styles.formActions}>
        <CustomButton
          style={{
            ...defaultButtonStyle,
            width: '200px',
          }}
          onClick={handleSaveClick}
          disabled={loading || volunteerLoading}
        >
          {loading ? 'Saving...' : 'Save Meet'}
        </CustomButton>
      </div>
    </div>
  );
};

MeetForm.propTypes = {
  clubId: PropTypes.string,
  mode: PropTypes.string,
  meetId: PropTypes.string,
  formValues: PropTypes.object.isRequired,
  handleInputChange: PropTypes.func.isRequired,
  handleTimeChange: PropTypes.func.isRequired,
  handleButton: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  loading: PropTypes.bool,
  sourceMeet: PropTypes.shape({
    meetID: PropTypes.string,
    meetName: PropTypes.string,
    meetDate: PropTypes.string,
  }),
};

export default MeetForm;
